<template>
  <div class="adm-shell adm-layout" :class="{ 'is-collapsed': collapsed }">
    <AdminSidebar
      :collapsed="collapsed"
      :drawer-open="drawerOpen"
      @toggle-collapse="toggleCollapse"
      @navigate="closeDrawer"
    />

    <!-- Fondo oscuro del sidebar en móvil: cierra el cajón al tocarlo. -->
    <div
      v-if="drawerOpen"
      class="adm-layout__backdrop"
      aria-hidden="true"
      @click="closeDrawer"
    ></div>

    <div class="adm-layout__main">
      <AdminTopbar @open-drawer="openDrawer" />
      <main class="adm-layout__content adm-scroll">
        <div class="adm-layout__inner">
          <slot />
        </div>
      </main>
    </div>

    <AdmToastHost />
  </div>
</template>

<script>
/**
 * Layout administrativo — plan 27_09_2026 §4.1.
 *
 * Reemplaza al layout público en las 8 rutas con `requiresAuth`: el operario
 * dejaba de ver la cabecera comercial ("Cotiza ahora"), el footer institucional
 * y el botón flotante de WhatsApp dentro del panel de gestión.
 *
 * Tres estados de sidebar, como fija el plan §6:
 *   >= 1024px  expandido (colapsable a iconos por preferencia del usuario)
 *   768-1023px colapsado a iconos
 *   < 768px    off-canvas sobre backdrop
 */
import { ref, onMounted, provide } from 'vue';
import AdminSidebar from '@/components/Admin/AdminSidebar.vue';
import AdminTopbar from '@/components/Admin/AdminTopbar.vue';
import AdmToastHost from '@/components/Admin/ui/AdmToastHost.vue';
import { useAuthUser } from '@/composables/useAuthUser.js';

const STORAGE_KEY = 'selk.admin.sidebarCollapsed';

export default {
  name: 'AdminLayout',
  components: { AdminSidebar, AdminTopbar, AdmToastHost },
  setup() {
    const collapsed = ref(false);
    const drawerOpen = ref(false);
    const { fetchUser } = useAuthUser();

    function toggleCollapse() {
      collapsed.value = !collapsed.value;
      try {
        localStorage.setItem(STORAGE_KEY, String(collapsed.value));
      } catch {
        // Modo privado o almacenamiento bloqueado: la preferencia no se
        // recuerda entre sesiones, pero el panel funciona igual.
      }
    }

    function openDrawer() { drawerOpen.value = true; }
    function closeDrawer() { drawerOpen.value = false; }

    onMounted(() => {
      try {
        collapsed.value = localStorage.getItem(STORAGE_KEY) === 'true';
      } catch {
        collapsed.value = false;
      }
      // El guard ya validó la sesión; esto sólo hidrata el nombre de la topbar.
      fetchUser();
    });

    provide('admSidebarCollapsed', collapsed);

    return { collapsed, drawerOpen, toggleCollapse, openDrawer, closeDrawer };
  },
};
</script>

<style scoped>
.adm-layout {
  display: grid;
  grid-template-columns: var(--adm-sidebar-width) minmax(0, 1fr);
  min-height: 100vh;
  transition: grid-template-columns 0.2s ease;
}

.adm-layout.is-collapsed {
  grid-template-columns: var(--adm-sidebar-collapsed) minmax(0, 1fr);
}

.adm-layout__main {
  display: flex;
  flex-direction: column;
  min-width: 0; /* sin esto, una tabla ancha estira el grid y rompe el layout */
}

.adm-layout__content {
  flex: 1;
  padding: var(--adm-content-pad);
  /* Sin `overflow-x: auto`: por especificación, si un eje deja de ser
     `visible` el otro pasa a `auto`, así que esto convertía el contenido en
     un scroller vertical que recortaba los desplegables abiertos cerca del
     borde. El scroll horizontal de las tablas lo resuelve `.adm-table-wrap`,
     y `min-width: 0` en `.adm-layout__main` ya evita que el grid se estire. */
}

.adm-layout__inner {
  max-width: var(--adm-content-max);
  margin-inline: auto;
}

.adm-layout__backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--adm-z-backdrop);
  background-color: rgba(44, 62, 80, 0.45);
}

/* Tablet: el sidebar se queda en iconos, sin opción de expandir. */
@media (max-width: 1023px) {
  .adm-layout,
  .adm-layout.is-collapsed {
    grid-template-columns: var(--adm-sidebar-collapsed) minmax(0, 1fr);
  }
}

/* Móvil: el sidebar sale del flujo y el contenido ocupa todo el ancho. */
@media (max-width: 767px) {
  .adm-layout,
  .adm-layout.is-collapsed {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
